import CloseIcon from '@mui/icons-material/Close'
import {
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
} from '@mui/material'
import axios from 'axios'
import config from 'config'
import { Form, Formik } from 'formik'
import logger from 'logger'
import T from 'prop-types'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import useSnackbar from 'components/organisms/Snackbar/useSnackbar'

import usePartnersGroups from 'hooks/usePartnersGroups'

import PartnerInfoFields from './PartnerInfoFields'

const COMMON_REQUIRED_FIELD = 'common.requiredField'
const PartnerInfoDialog = ({
  partner,
  open,
  handleClose,
  refetch,
  allowedRoles,
}) => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const PartnerSchema = Yup.object().shape({
    partnerId: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    address: Yup.string(),
    name: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
    vat: Yup.string(),
    status: Yup.string().required(t(COMMON_REQUIRED_FIELD)),
  })

  const { partnersGroups, fetchPartnersGroups } = usePartnersGroups({})

  useEffect(() => {
    fetchPartnersGroups()
  }, [fetchPartnersGroups])

  if (!open) return null

  const onSubmit = async (values) => {
    const data = {
      partnerId: values.partnerId,
      name: values.name,
      address: values.address,
      vat: values.vat,
      state: values.status,
    }

    if (!partner) {
      try {
        await axios.post(`${config.apiBaseUrl}partners`, data)

        showSnackbarMessage({
          severity: 'success',
          message: t('users.createPartner.success'),
        })
      } catch (e) {
        logger.error('[Error] - partner creation', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('users.createPartner.error'),
        })
      }
    } else {
      try {
        await axios.patch(
          `${config.apiBaseUrl}partners/${partner.partnerId}`,
          data
        )

        showSnackbarMessage({
          severity: 'success',
          message: t('users.editPartner.success'),
        })
      } catch (e) {
        logger.error('[Error] - partner editing', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('users.editPartner.error'),
        })
      }
    }

    refetch()
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Formik
        validationSchema={PartnerSchema}
        enableReinitialize={true}
        initialValues={{
          name: partner?.name ?? '',
          partnerId: partner?.partnerId ?? '',
          address: partner?.address ?? '',
          vat: partner?.vat ?? '',
          status: partner?.state ?? '',
        }}
        onSubmit={onSubmit}
      >
        <Form>
          <DialogTitle>
            {t(
              partner
                ? 'users.userInfoDialog.title2Partner'
                : 'users.userInfoDialog.title1Partner'
            )}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <PartnerInfoFields
            allowedRoles={allowedRoles}
            partnersGroups={partnersGroups.items}
          />
          <DialogActions>
            <Button
              variant="text"
              onClick={handleClose}
              data-testid={'partner-dialog-cancel-button'}
            >
              {t('users.userInfoDialog.cancelButton')}
            </Button>
            <Button
              variant="contained"
              type="submit"
              data-testid={'partner-dialog-submit-button'}
            >
              {t(
                partner
                  ? 'users.userInfoDialog.confirmButton2'
                  : 'users.userInfoDialog.confirmButton1'
              )}
            </Button>
          </DialogActions>
        </Form>
      </Formik>
    </Dialog>
  )
}

PartnerInfoDialog.propTypes = {
  partner: T.any, // TODO
  open: T.bool,
  handleClose: T.func,
  refetch: T.func,
}

export default PartnerInfoDialog
